import type { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import AdminAuditPage from './admin/AdminAuditPage.js';
import AdminCostsPage from './admin/AdminCostsPage.js';
import AdminGroupDetailPage from './admin/AdminGroupDetailPage.js';
import AdminGroupsPage from './admin/AdminGroupsPage.js';
import AdminQueuePage from './admin/AdminQueuePage.js';
import AdminUserDetailPage from './admin/AdminUserDetailPage.js';
import AdminUsersPage from './admin/AdminUsersPage.js';
import AssistantPage from './pages/AssistantPage.js';
import ConnectionRunsPage from './pages/ConnectionRunsPage.js';
import ConnectionsPage from './pages/ConnectionsPage.js';
import { ExecutiveModule, ExecutiveNotFound } from './pages/ExecutivePage.js';
import MeetingPage from './pages/MeetingPage.js';
import MeetingsPage from './pages/MeetingsPage.js';
import OverviewPage from './pages/OverviewPage.js';
import ProjectPage from './pages/ProjectPage.js';
import ProjectsPage from './pages/ProjectsPage.js';
import SettingsPage from './pages/SettingsPage.js';
import TodosPage from './pages/TodosPage.js';
import { AdminIndex, AdminNotFound, AdminRoute } from './shell/admin.js';
import { DevSignInPage, NotFoundPage, SignedOutPage } from './shell/pages.js';

/**
 * The route registry (spec §13). The paths live in paths.ts (a leaf module, so a page may read
 * them at module load without meeting this file's import cycle) and are re-exported here; the
 * page components are owned by CLIENT-PAGES (pages/) and CLIENT-ADMIN (admin/).
 */
export { PATHS } from './paths.js';

/** Routes rendered inside the authenticated shell (behind MeProvider). */
export function appRoutes(): ReactElement[] {
  return [
    <Route key="overview" index element={<OverviewPage />} />,
    <Route key="todos" path="todos" element={<TodosPage />} />,
    <Route key="meetings" path="meetings" element={<MeetingsPage />} />,
    <Route key="meeting" path="meetings/:id" element={<MeetingPage />} />,
    <Route key="projects" path="projects" element={<ProjectsPage />} />,
    <Route key="project" path="projects/:id" element={<ProjectPage />} />,
    <Route key="assistant" path="assistant" element={<AssistantPage />} />,
    // The executive module (ADR 0019; design §2.13): every route of it renders the section strip, its dead ends included.
    <Route key="executive" path="executive">
      <Route index element={<ExecutiveModule section="report" />} />
      <Route path="pulse" element={<ExecutiveModule section="pulse" />} />
      <Route path="matters" element={<ExecutiveModule section="matters" />} />
      <Route path="coverage" element={<ExecutiveModule section="coverage" />} />
      <Route path="*" element={<ExecutiveNotFound />} />
    </Route>,
    <Route key="connections" path="connections" element={<ConnectionsPage />} />,
    <Route key="connection-runs" path="connections/:system/runs" element={<ConnectionRunsPage />} />,
    <Route key="settings" path="settings" element={<SettingsPage />} />,
    <Route key="admin" path="admin">
      <Route index element={<AdminIndex />} />
      <Route path="users" element={<AdminRoute section="users"><AdminUsersPage /></AdminRoute>} />
      <Route path="users/:id" element={<AdminRoute section="users"><AdminUserDetailPage /></AdminRoute>} />
      <Route path="groups" element={<AdminRoute section="groups"><AdminGroupsPage /></AdminRoute>} />
      <Route path="groups/:id" element={<AdminRoute section="groups"><AdminGroupDetailPage /></AdminRoute>} />
      <Route path="audit" element={<AdminRoute section="audit"><AdminAuditPage /></AdminRoute>} />
      <Route path="costs" element={<AdminRoute section="costs"><AdminCostsPage /></AdminRoute>} />
      <Route path="queue" element={<AdminRoute section="queue"><AdminQueuePage /></AdminRoute>} />
      <Route path="*" element={<AdminNotFound />} />
    </Route>,
    <Route key="not-found" path="*" element={<NotFoundPage />} />,
  ];
}

/** Routes that render without a session. */
export function publicRoutes(): ReactElement[] {
  return [
    <Route key="signed-out" path="signed-out" element={<SignedOutPage />} />,
    <Route key="dev-sign-in" path="dev-sign-in" element={<DevSignInPage />} />,
  ];
}
